/**
 * Language Context - Centralized translation management
 * 
 * Loads translations from JSON files and provides a t(key) function
 * to access translated strings throughout the application.
 * 
 * Only employee UIs use translations - admin interfaces remain in Japanese.
 * 
 * Usage:
 * const { t, language, setLanguage } = useLanguage();
 * <h1>{t('menu.title')}</h1>
 */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import jaTranslations from '../translations/ja.json';
import enTranslations from '../translations/en.json';
import viTranslations from '../translations/vi.json';

export type Language = 'ja' | 'en' | 'vi';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  /** Returns translation for a specific language (e.g. for showing multiple languages in one view) */
  tForLang: (lang: Language, key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Translation data loaded from JSON files
const translations = {
  ja: jaTranslations,
  en: enTranslations,
  vi : viTranslations
};

interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  // Get initial language from localStorage or default to Japanese
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('app-language');
    return (saved === 'ja' || saved === 'en' || saved === 'vi') ? saved : 'ja';
  });

  // Save language to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('app-language', language);
  }, [language]);

  const getByKey = (lang: Language, key: string): string => {
    const keys = key.split('.');
    let value: unknown = translations[lang];
    for (const k of keys) {
      if (value && typeof value === 'object' && k in (value as object)) {
        value = (value as Record<string, unknown>)[k];
      } else {
        return key;
      }
    }
    return typeof value === 'string' ? value : key;
  };

  /**
   * Translation function - gets translated text by key path in current language
   */
  const t = (key: string): string => getByKey(language, key);

  /**
   * Get translation for a specific language (e.g. to show JA/EN/VI in one consent box)
   */
  const tForLang = (lang: Language, key: string): string => getByKey(lang, key);

  /**
   * Set the application language
   * @param lang - Language code ('ja' or 'en')
   */
  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, tForLang }}>
      {children}
    </LanguageContext.Provider>
  );
}

/**
 * Hook to access language context
 * Use this in any component to get language and translation function
 * 
 * @example
 * const { t, language, setLanguage } = useLanguage();
 * <h1>{t('menu.title')}</h1>
 */
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
